import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import LottieAnimation from '../LottieAnimation/LottieAnimation';

import {
  CardContainer,
  Layout,
  Leading,
  Trailing,
  Title,
  TitleIcon,
  Subtitle,
  Link,
  CloseTarget,
  LottieContainer
} from './Card.styles';
import IconX from '@veneer/core/dist/scripts/icons/icon_x';
import { isJsonFile } from '../../utils/functional';

const getComputedfontSize = elRef => {
  return elRef && elRef.current
    ? Number(window.getComputedStyle(elRef.current).fontSize.split('px')[0])
    : 0;
};

const Card = props => {
  const {
    id,
    onCardClick,
    backgroundImage,
    titleIcon,
    title,
    subtitle,
    link,
    dismissable,
    onClose,
    transitionName,
    isLoading
  } = props;
  const isButtonClicked = useRef(false);
  const subTitleRef = useRef();
  const isAnimation = isJsonFile(backgroundImage);
  const regex = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/;

  const onClick = () => {
    if (!isButtonClicked.current) {
      isButtonClicked.current = true;
      onCardClick();
      setTimeout(() => {
        isButtonClicked.current = false;
      }, 500);
    }
  };
  return (
    <CardContainer role='listitem' tabIndex='0'>
      <Layout onClick={onClick}>
        {isAnimation ? (
          <LottieContainer id={`${id}_image`}>
            <LottieAnimation filePath={backgroundImage} />
          </LottieContainer>
        ) : (
          <Leading id={`${id}_image`} image={`url(${backgroundImage})`} />
        )}
        <Trailing>
          <div>
            {title && regex.test(title) ? (
              <Title
                id={`${id}_title`}
                title={title}
                dangerouslySetInnerHTML={{ __html: title }}></Title>
            ) : (
              <Title id={`${id}_title`} title={title}>
                {title}
              </Title>
            )}
            {subtitle && regex.test(subtitle) ? (
              <Subtitle
                id={`${id}_subtitle`}
                title={subtitle}
                ref={subTitleRef}
                computedFontSize={getComputedfontSize(subTitleRef)}
                dangerouslySetInnerHTML={{ __html: subtitle }}></Subtitle>
            ) : (
              <Subtitle
                id={`${id}_subtitle`}
                title={subtitle}
                ref={subTitleRef}
                computedFontSize={getComputedfontSize(subTitleRef)}>
                {subtitle}
              </Subtitle>
            )}
            <Link id={`${id}_link`} title={link}>
              {link}
            </Link>
          </div>
        </Trailing>
      </Layout>
      {dismissable && !isLoading && (
        <CloseTarget
          onClick={onClose}
          id={`${id}_close_button`}
          data-testid={`${id}_close_button`}
          aria-label={`${title} close`}>
          <IconX filled alt='' />
        </CloseTarget>
      )}
    </CardContainer>
  );
};

Card.propTypes = {
  /**
   * Tile Id
   */
  id: PropTypes.string,
  /**
   * Image to show
   */
  backgroundImage: PropTypes.string,
  /**
   * Title of card
   */
  titleIcon: PropTypes.string,
  /**
   * Title of card
   */
  title: PropTypes.string,
  /**
   * Subtitle of card
   */
  subtitle: PropTypes.string,
  /**
   * Title of card
   */
  link: PropTypes.string,
  /**
   * Title of card
   */
  dismissable: PropTypes.bool,
  /**
   * Optional close handler
   */
  onClose: PropTypes.func,
  /**
   * Optional link handler
   */
  onCardClick: PropTypes.func,

  isLoading: PropTypes.bool
};

Card.defaultProps = {
  id: null,
  backgroundImage: null,
  dismissible: true,
  titleIcon: null,
  title: '',
  subtitle: '',
  link: '',
  onClose: undefined,
  onCardClick: undefined,
  isLoading: false
};

export default Card;
